import { useMemo } from "react";
import { RefreshCw, Clock, Wrench, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";

interface Step {
  id: number;
  phase: number;
  phaseName: string;
  title: string;
  description: string;
  isSafetyCritical: boolean;
  isDocumentation?: boolean;
  estimatedTime: string;
  tools: string[];
}

interface StepsOverviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  steps: Step[];
  currentStepIndex: number;
  completedSteps: number[];
  onNavigateToStep: (index: number) => void;
  readOnly?: boolean;
}

export function StepsOverviewDialog({
  open,
  onOpenChange,
  steps,
  currentStepIndex,
  completedSteps,
  onNavigateToStep,
  readOnly = false,
}: StepsOverviewDialogProps) {
  const totalSteps = steps.length;
  const completedCount = completedSteps.length;
  const remainingCount = totalSteps - completedCount;
  const progressPercent = Math.round((completedCount / totalSteps) * 100);

  // Derive unique phases from steps data
  const phases = useMemo(() => {
    const seen = new Map<number, string>();
    for (const step of steps) {
      if (!seen.has(step.phase)) {
        seen.set(step.phase, step.phaseName || `Phase ${step.phase}`);
      }
    }
    return Array.from(seen.entries()).map(([num, name]) => ({ num, name }));
  }, [steps]);

  const getPhaseSteps = (phaseNum: number) => steps.filter((s) => s.phase === phaseNum);

  const getPhaseProgress = (phaseNum: number) => {
    const phaseSteps = getPhaseSteps(phaseNum);
    const completed = phaseSteps.filter((s) => completedSteps.includes(s.id)).length;
    return { completed, total: phaseSteps.length };
  };

  const getPhaseMinutes = (phaseNum: number): number => {
    return getPhaseSteps(phaseNum).reduce((sum, s) => {
      const match = /^(\d+(?:\.\d+)?)\s*min/i.exec(s.estimatedTime || "");
      return sum + (match ? Number(match[1]) : 0);
    }, 0);
  };

  const getStepStatus = (step: Step, index: number) => {
    if (completedSteps.includes(step.id)) return "completed";
    if (index === currentStepIndex) return "current";
    return "pending";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl h-[85vh] overflow-hidden p-0">
        <DialogHeader className="shrink-0 p-6 pb-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-refurbish/10 flex items-center justify-center">
              <RefreshCw className="w-5 h-5 text-refurbish" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold">Refurbishment Process Overview</DialogTitle>
              <p className="text-sm text-muted-foreground">
                View all refurbishment steps organized by phase, with current progress and completion status for each step. Click on any step to navigate directly to it.
              </p>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="min-h-0 flex-1">
          <div className="p-6 pb-8 space-y-6">
            {/* Overall Progress */}
            <div className="bg-muted/50 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="font-medium text-foreground">Overall Progress</span>
                <span className="text-lg font-bold text-refurbish">{progressPercent}%</span>
              </div>
              <Progress value={progressPercent} className="h-2 mb-3" />
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-xl font-bold text-refurbish">{completedCount}</p>
                  <p className="text-xs text-muted-foreground">Completed</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-foreground">{remainingCount}</p>
                  <p className="text-xs text-muted-foreground">Remaining</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-refurbish">{completedCount}</p>
                  <p className="text-xs text-muted-foreground">Documented</p>
                </div>
              </div>
            </div>

            {/* Phases */}
            {phases.map(({ num: phaseNum, name: phaseName }) => {
              const phaseSteps = getPhaseSteps(phaseNum);
              const progress = getPhaseProgress(phaseNum);
              const phaseMinutes = getPhaseMinutes(phaseNum);

              return (
                <div key={phaseNum} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-foreground flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm font-medium flex items-center justify-center flex-shrink-0">
                        {phaseNum}
                      </span>
                      {phaseName}
                    </h3>
                    <div className="flex items-center gap-2">
                      {phaseMinutes > 0 && (
                        <span className="px-2 py-1 bg-muted text-muted-foreground text-xs font-medium rounded-full flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {phaseMinutes} min
                        </span>
                      )}
                      <span className="px-2 py-1 bg-refurbish/10 text-refurbish text-xs font-medium rounded-full">
                        {progress.completed}/{progress.total} Complete
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {phaseSteps.map((step) => {
                      const stepIndex = steps.findIndex((s) => s.id === step.id);
                      const status = getStepStatus(step, stepIndex);

                      const Wrapper = readOnly ? 'div' : 'button';
                      const wrapperProps = readOnly
                        ? {}
                        : {
                            onClick: () => {
                              onNavigateToStep(stepIndex);
                              onOpenChange(false);
                            },
                          };

                      return (
                        <Wrapper
                          key={step.id}
                          {...wrapperProps}
                          className={`w-full text-left p-4 bg-card border border-border rounded-xl ${!readOnly ? 'hover:border-primary/50 cursor-pointer' : ''} transition-colors`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-3 flex-1">
                              <span
                                className={`w-6 h-6 rounded-full text-xs font-medium flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                  status === "completed"
                                    ? "bg-refurbish text-refurbish-foreground"
                                    : status === "current"
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-muted text-muted-foreground"
                                }`}
                              >
                                {step.id}
                              </span>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                  <span className="font-medium text-foreground">{step.title}</span>
                                  {!readOnly && status === "current" && (
                                    <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full">
                                      Current
                                    </span>
                                  )}
                                  {step.isDocumentation && (
                                    <span className="px-2 py-0.5 bg-muted text-muted-foreground text-xs rounded-full flex items-center gap-1">
                                      <FileText className="w-3 h-3" />
                                      Documentation
                                    </span>
                                  )}
                                  {step.isSafetyCritical && (
                                    <span className="px-2 py-0.5 bg-destructive text-destructive-foreground text-xs rounded-full">
                                      Safety Critical
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground mb-2">{step.description}</p>
                                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                  {step.estimatedTime && (
                                    <span className="flex items-center gap-1">
                                      <Clock className="w-3 h-3" />
                                      {step.estimatedTime}
                                    </span>
                                  )}
                                  {step.tools.length > 0 && (
                                    <span className="flex items-center gap-1">
                                      <Wrench className="w-3 h-3" />
                                      {step.tools.join(", ")}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p
                                className={`text-sm font-medium ${
                                  status === "completed"
                                    ? "text-refurbish"
                                    : status === "current"
                                    ? "text-primary"
                                    : "text-muted-foreground"
                                }`}
                              >
                                {status === "completed"
                                  ? "Completed"
                                  : status === "current"
                                  ? "Current Step"
                                  : "Pending"}
                              </p>
                              {!readOnly && (
                                <p className="text-xs text-muted-foreground">Click to navigate</p>
                              )}
                            </div>
                          </div>
                        </Wrapper>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}